import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { useState } from "react";

import PlaceSearch from "./PlaceSearch";
import LocationSizeControls from "./steps/LocationSizeControls";
import type { CreateProjectData } from "./types";
import type { ProjectAreaPreview } from "./useProjectAreaPreview";
import CreateProjectStepContent from "./CreateProjectStepContent";
import { CREATE_PROJECT_STEPS, LAST_STEP, canProceedFrom } from "./wizardSteps";

const TEAL = "#00d4ad";

interface CreateProjectPaneProps {
  data: CreateProjectData;
  step: number;
  onStepChange: (step: number) => void;
  onUpdateData: (updates: Partial<CreateProjectData>) => void;
  /** The project's area as drawn on the map beside this panel. */
  area: ProjectAreaPreview;
  /** Where the project will be filed ("All projects" at the top level). */
  folderLabel: string;
  onCancel: () => void;
  onCreated: () => void;
}

/**
 * The create-project steps in the listing's right-hand pane, with the listing
 * map left of it doing the map work: clicks there place the project on the
 * Location & Size step, and the area, tiles and property boundaries are drawn
 * there throughout.
 */
export default function CreateProjectPane({
  data,
  step,
  onStepChange,
  onUpdateData,
  area,
  folderLabel,
  onCancel,
  onCreated,
}: CreateProjectPaneProps) {
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const next = () => onStepChange(Math.min(step + 1, LAST_STEP));
  const back = () => onStepChange(Math.max(step - 1, 0));
  // Only ask when there's work to lose beyond the name just typed.
  const requestCancel = () => {
    if (step > 0 || data.description.trim()) setConfirmingCancel(true);
    else onCancel();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, backgroundColor: "#fff" }}>
      {/* Header: which project, where it goes, and the way out. */}
      <Box sx={{ px: 3, pt: 2, pb: 1.5, borderBottom: 1, borderColor: "divider" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="overline" sx={{ color: "text.secondary", lineHeight: 1.6 }}>
              New project
            </Typography>
            <Typography variant="h6" noWrap sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {data.name.trim() || "New Project"}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary", mt: 0.25 }}>
              <FolderOutlinedIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption" noWrap>
                {folderLabel}
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Cancel">
            <IconButton onClick={requestCancel} aria-label="Cancel the new project">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Step indicator: one bar per step; done steps can be clicked to go back. */}
        <Box sx={{ display: "flex", gap: 0.5, mt: 1.5 }}>
          {CREATE_PROJECT_STEPS.map((label, i) => (
            <Tooltip key={label} title={label}>
              <Box
                role={i < step ? "button" : undefined}
                aria-label={i < step ? `Back to ${label}` : undefined}
                onClick={i < step ? () => onStepChange(i) : undefined}
                sx={{
                  flex: 1,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: i <= step ? TEAL : "#e5e7eb",
                  opacity: i < step ? 0.6 : 1,
                  cursor: i < step ? "pointer" : "default",
                  "&:hover": i < step ? { opacity: 1 } : undefined,
                }}
              />
            </Tooltip>
          ))}
        </Box>
        <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "text.secondary" }}>
          Step {step + 1} of {CREATE_PROJECT_STEPS.length} · {CREATE_PROJECT_STEPS[step]}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", backgroundColor: "#fafbfc" }}>
        <CreateProjectStepContent
          step={step}
          data={data}
          onNext={next}
          onBack={back}
          onUpdateData={onUpdateData}
          onCreated={onCreated}
          locationStep={
            <Box
              sx={{
                p: 3,
                maxWidth: 720,
                mx: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <LocationSizeControls
                data={data}
                onUpdateData={onUpdateData}
                area={area}
                searchSlot={<PlaceSearch />}
              />
            </Box>
          }
        />
      </Box>

      {/* The last step has its own footer with the Create button. */}
      {step < LAST_STEP && (
        <Box
          sx={{
            px: 3,
            py: 2,
            backgroundColor: "#f8f9fa",
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            variant="outlined"
            onClick={back}
            disabled={step === 0}
            sx={{
              borderColor: "#e0e0e0",
              color: "#666",
              "&:hover": { backgroundColor: "#f5f5f5", borderColor: "#ccc" },
            }}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            onClick={next}
            disabled={!canProceedFrom(step, data)}
            sx={{
              backgroundColor: TEAL,
              "&:hover": { backgroundColor: "#00b89c" },
              "&:disabled": { backgroundColor: "#e0e0e0", color: "#999" },
            }}
          >
            Next
          </Button>
        </Box>
      )}

      <Dialog open={confirmingCancel} onClose={() => setConfirmingCancel(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Discard this project?</DialogTitle>
        <DialogContent>
          <DialogContentText>Nothing has been created yet, so what you've entered will be lost.</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmingCancel(false)} color="inherit">
            Keep going
          </Button>
          <Button
            onClick={() => {
              setConfirmingCancel(false);
              onCancel();
            }}
            variant="contained"
            color="error"
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
